import { FC } from 'react';
import Highcharts from 'highcharts';
import Chart from 'highcharts-react-official';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { themed, CHART_HEIGHT } from './chart-theme';
import { statusHex, titleize } from './format';
import type {
  AgentCostSeries,
  AgentCostSummary,
  ChartSeries,
  ModelBreakdown,
} from './types';
import styles from './page.module.scss';

type OnSelectAgent = ((agentType: string) => void) | undefined;

// Drill-down for charts whose x-axis categories ARE the agents (click column ->
// that agent's runs), resolved by category index.
const byIndexDrill = (
  agents: AgentCostSummary[],
  onSelect: OnSelectAgent
): Highcharts.Options['plotOptions'] => {
  if (!onSelect) return {};
  return {
    series: {
      cursor: 'pointer',
      point: {
        events: {
          click(this: Highcharts.Point) {
            const agent = agents[this.index];
            if (agent) onSelect(agent.agentType);
          },
        },
      },
    },
  };
};

// Drill-down for charts whose SERIES carry the agent (custom: { agentType }):
// click any point -> that series' agent. Sibling of byIndexDrill for the
// stacked multi-series charts.
const byCustomAgentDrill = (
  onSelect: OnSelectAgent
): Highcharts.Options['plotOptions'] => {
  if (!onSelect) return {};
  return {
    series: {
      cursor: 'pointer',
      point: {
        events: {
          click(this: Highcharts.Point) {
            const agentType = (
              this.series.options.custom as { agentType?: string } | undefined
            )?.agentType;
            if (agentType) onSelect(agentType);
          },
        },
      },
    },
  };
};

const timeBase = (dates: string[], yTitle: string): Highcharts.Options => ({
  chart: { height: CHART_HEIGHT },
  xAxis: { categories: dates, title: { text: 'day' }, tickInterval: 7 },
  yAxis: { min: 0, title: { text: yTitle } },
  legend: { enabled: true },
});

// ── Trend charts (over time) ──

const StatusChart: FC<{ dates: string[]; series: ChartSeries[] }> = ({
  dates,
  series,
}) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      ...timeBase(dates, 'count'),
      chart: { height: CHART_HEIGHT, type: 'area' },
      plotOptions: {
        area: { stacking: 'normal', lineWidth: 1, legendSymbol: 'rectangle' },
      },
      tooltip: { shared: true },
      series: series.map((s) => ({
        type: 'area',
        name: titleize(s.name),
        data: s.data,
        color: statusHex(s.name),
      })) as Highcharts.SeriesOptionsType[],
    })}
  />
);

const CostChart: FC<{
  dates: string[];
  series: ChartSeries[];
  onSelectAgent: OnSelectAgent;
}> = ({ dates, series, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      ...timeBase(dates, 'cost ($)'),
      chart: { height: CHART_HEIGHT, type: 'column' },
      plotOptions: {
        column: { stacking: 'normal' },
        ...byCustomAgentDrill(onSelectAgent),
      },
      tooltip: { shared: true, valuePrefix: '$', valueDecimals: 2 },
      series: series.map((s) => ({
        type: 'column',
        name: titleize(s.name),
        data: s.data,
        custom: { agentType: s.name },
      })) as Highcharts.SeriesOptionsType[],
    })}
  />
);

const FailureRateChart: FC<{ dates: string[]; series: ChartSeries[] }> = ({
  dates,
  series,
}) => {
  // Failure rate is over *completed* runs (succeeded + failed) per day, so
  // in-progress runs (pending/processing) don't deflate it — consistent with
  // the Success Rate KPI.
  const failed = series.find((s) => s.name === 'failed')?.data ?? [];
  const succeeded = series.find((s) => s.name === 'processed')?.data ?? [];
  const rate = dates.map((_, i) => {
    const completed = (succeeded[i] ?? 0) + (failed[i] ?? 0);
    return completed > 0 ? Number(((failed[i] ?? 0) / completed) * 100) : 0;
  });

  return (
    <Chart
      highcharts={Highcharts}
      options={themed({
        ...timeBase(dates, 'failure rate (%)'),
        chart: { height: CHART_HEIGHT, type: 'line' },
        yAxis: { min: 0, max: 100, title: { text: 'failure rate (%)' } },
        legend: { enabled: false },
        tooltip: { valueSuffix: '%', valueDecimals: 1 },
        series: [
          {
            type: 'line',
            name: 'Failure rate',
            data: rate,
            color: statusHex('failed'),
            lineWidth: 1,
          },
        ] as Highcharts.SeriesOptionsType[],
      })}
    />
  );
};

const FailuresByAgentChart: FC<{
  dates: string[];
  series: ChartSeries[];
  onSelectAgent: OnSelectAgent;
}> = ({ dates, series, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      ...timeBase(dates, 'failed runs'),
      chart: { height: CHART_HEIGHT, type: 'column' },
      plotOptions: {
        column: { stacking: 'normal' },
        ...byCustomAgentDrill(onSelectAgent),
      },
      tooltip: { shared: true },
      series: series.map((s) => ({
        type: 'column',
        name: titleize(s.name),
        data: s.data,
        custom: { agentType: s.name },
      })) as Highcharts.SeriesOptionsType[],
    })}
  />
);

// ── Breakdown charts (per-agent snapshots) ──

const CostShareChart: FC<{
  agents: AgentCostSummary[];
  onSelectAgent: OnSelectAgent;
}> = ({ agents, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      chart: { type: 'pie', height: CHART_HEIGHT },
      tooltip: {
        pointFormat: '<b>${point.y:,.2f}</b> ({point.percentage:.1f}%)',
      },
      plotOptions: {
        pie: {
          innerSize: '55%',
          cursor: onSelectAgent ? 'pointer' : undefined,
          dataLabels: {
            enabled: true,
            format: '{point.name}: ${point.y:,.2f}',
          },
          point: {
            events: {
              click(this: Highcharts.Point) {
                const agentType = (
                  this.options.custom as { agentType?: string } | undefined
                )?.agentType;
                if (agentType && onSelectAgent) onSelectAgent(agentType);
              },
            },
          },
        },
      },
      series: [
        {
          type: 'pie',
          name: 'Cost',
          data: agents.map((a) => ({
            name: titleize(a.agentType),
            y: a.totalCost,
            custom: { agentType: a.agentType },
          })),
        },
      ] as Highcharts.SeriesOptionsType[],
    })}
  />
);

const DurationChart: FC<{
  agents: AgentCostSummary[];
  onSelectAgent: OnSelectAgent;
}> = ({ agents, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      chart: { type: 'column', height: CHART_HEIGHT },
      xAxis: { categories: agents.map((a) => titleize(a.agentType)) },
      yAxis: { min: 0, title: { text: 'seconds' } },
      tooltip: { shared: true, valueSuffix: 's', valueDecimals: 1 },
      plotOptions: byIndexDrill(agents, onSelectAgent),
      series: [
        {
          type: 'column',
          name: 'Avg',
          data: agents.map((a) => a.avgDurationSeconds ?? 0),
        },
        {
          type: 'column',
          name: 'P95',
          data: agents.map((a) => a.p95DurationSeconds ?? 0),
        },
      ] as Highcharts.SeriesOptionsType[],
    })}
  />
);

const TOKEN_TYPES: Array<{ key: keyof AgentCostSummary; label: string }> = [
  { key: 'inputTokens', label: 'Input' },
  { key: 'outputTokens', label: 'Output' },
  { key: 'cacheWriteTokens', label: 'Cache write' },
  { key: 'cacheReadTokens', label: 'Cache read' },
];

const TokensByTypeChart: FC<{
  agents: AgentCostSummary[];
  onSelectAgent: OnSelectAgent;
}> = ({ agents, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      chart: { type: 'column', height: CHART_HEIGHT },
      xAxis: { categories: agents.map((a) => titleize(a.agentType)) },
      yAxis: { min: 0, title: { text: 'tokens' } },
      plotOptions: Highcharts.merge(byIndexDrill(agents, onSelectAgent), {
        column: { stacking: 'normal' },
      }),
      tooltip: { shared: true },
      series: TOKEN_TYPES.map((t) => ({
        type: 'column',
        name: t.label,
        data: agents.map((a) => Number(a[t.key]) || 0),
      })) as Highcharts.SeriesOptionsType[],
    })}
  />
);

const COST_TYPES: Array<{
  key: keyof AgentCostSummary['costByType'];
  label: string;
}> = [
  { key: 'input', label: 'Input' },
  { key: 'output', label: 'Output' },
  { key: 'cacheWrite', label: 'Cache write' },
  { key: 'cacheRead', label: 'Cache read' },
];

const CostByTypeChart: FC<{
  agents: AgentCostSummary[];
  onSelectAgent: OnSelectAgent;
}> = ({ agents, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      chart: { type: 'column', height: CHART_HEIGHT },
      xAxis: { categories: agents.map((a) => titleize(a.agentType)) },
      yAxis: { min: 0, title: { text: 'cost ($)' } },
      plotOptions: Highcharts.merge(byIndexDrill(agents, onSelectAgent), {
        column: { stacking: 'normal' },
      }),
      tooltip: { shared: true, valuePrefix: '$', valueDecimals: 2 },
      series: COST_TYPES.map((t) => ({
        type: 'column',
        name: t.label,
        data: agents.map((a) => a.costByType?.[t.key] ?? 0),
      })) as Highcharts.SeriesOptionsType[],
    })}
  />
);

const CostPerRunChart: FC<{
  agents: AgentCostSummary[];
  onSelectAgent: OnSelectAgent;
}> = ({ agents, onSelectAgent }) => (
  <Chart
    highcharts={Highcharts}
    options={themed({
      chart: { type: 'column', height: CHART_HEIGHT },
      xAxis: { categories: agents.map((a) => titleize(a.agentType)) },
      yAxis: { min: 0, title: { text: 'cost per run ($)' } },
      legend: { enabled: false },
      tooltip: { valuePrefix: '$', valueDecimals: 4 },
      plotOptions: byIndexDrill(agents, onSelectAgent),
      series: [
        {
          type: 'column',
          name: 'Cost per run',
          colorByPoint: true,
          data: agents.map((a) => {
            return a.totalRuns > 0 ? a.totalCost / a.totalRuns : 0;
          }),
        },
      ] as Highcharts.SeriesOptionsType[],
    })}
  />
);

const CostByModelChart: FC<{ models: ModelBreakdown[] }> = ({ models }) => {
  // Unpriced models cost $0, so they're absent from the cost pie. List them
  // separately so a missing model reads as "unknown price", not "free".
  const unpriced = models.filter((m) => m.unpriced);
  return (
    <>
      <Chart
        highcharts={Highcharts}
        options={themed({
          chart: { type: 'pie', height: CHART_HEIGHT },
          tooltip: {
            pointFormat: '<b>${point.y:,.2f}</b> ({point.percentage:.1f}%)',
          },
          plotOptions: {
            pie: {
              innerSize: '55%',
              dataLabels: {
                enabled: true,
                format: '{point.name}: ${point.y:,.2f}',
              },
            },
          },
          series: [
            {
              type: 'pie',
              name: 'Cost',
              data: models
                .filter((m) => m.cost > 0)
                // Defensive: if a priced model is also flagged unpriced, mark it.
                .map((m) => ({
                  name: m.unpriced ? `${m.model} (unpriced)` : m.model,
                  y: m.cost,
                })),
            },
          ] as Highcharts.SeriesOptionsType[],
        })}
      />
      {unpriced.length > 0 && (
        <div className={styles.chartNote}>
          Unpriced (cost unknown, shown as $0):{' '}
          {unpriced.map((m) => `${m.model} (${m.runCount} runs)`).join(', ')}
        </div>
      )}
    </>
  );
};

const ChartCard: FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className={styles.chartCard}>
    <div className={styles.chartTitle}>{title}</div>
    {children}
  </div>
);

export const AgentCostCharts: FC<{
  series: AgentCostSeries;
  agents: AgentCostSummary[];
  models: ModelBreakdown[];
  onSelectAgent?: (agentType: string) => void;
}> = ({ series, agents, models, onSelectAgent }) => {
  const hasTrends = !!series?.dates?.length;
  const hasAgents = agents.length > 0;
  if (!hasTrends && !hasAgents) return null;

  return (
    <>
      {hasTrends && (
        <Accordion className={styles.chartsAccordion} defaultExpanded>
          <AccordionSummary className={styles.chartsAccordionTitle}>
            Trends
          </AccordionSummary>
          <AccordionDetails>
            <div className={styles.chartGrid}>
              <ChartCard title="Agent runs by status over time">
                <StatusChart
                  dates={series.dates}
                  series={series.runsByStatus}
                />
              </ChartCard>
              <ChartCard title="Cost by agent over time">
                <CostChart
                  dates={series.dates}
                  series={series.costByAgent}
                  onSelectAgent={onSelectAgent}
                />
              </ChartCard>
              <ChartCard title="Failure rate over time">
                <FailureRateChart
                  dates={series.dates}
                  series={series.runsByStatus}
                />
              </ChartCard>
              <ChartCard title="Failures by agent over time">
                <FailuresByAgentChart
                  dates={series.dates}
                  series={series.failuresByAgent}
                  onSelectAgent={onSelectAgent}
                />
              </ChartCard>
            </div>
          </AccordionDetails>
        </Accordion>
      )}

      {hasAgents && (
        <Accordion className={styles.chartsAccordion} defaultExpanded>
          <AccordionSummary className={styles.chartsAccordionTitle}>
            Breakdowns
          </AccordionSummary>
          <AccordionDetails>
            <div className={styles.chartGrid}>
              <ChartCard title="Cost share by agent">
                <CostShareChart agents={agents} onSelectAgent={onSelectAgent} />
              </ChartCard>
              <ChartCard title="Run duration by agent (avg vs p95)">
                <DurationChart agents={agents} onSelectAgent={onSelectAgent} />
              </ChartCard>
              <ChartCard title="Token usage by type per agent">
                <TokensByTypeChart
                  agents={agents}
                  onSelectAgent={onSelectAgent}
                />
              </ChartCard>
              <ChartCard title="Cost by token type per agent">
                <CostByTypeChart
                  agents={agents}
                  onSelectAgent={onSelectAgent}
                />
              </ChartCard>
              <ChartCard title="Cost per run by agent">
                <CostPerRunChart
                  agents={agents}
                  onSelectAgent={onSelectAgent}
                />
              </ChartCard>
              {models.some((m) => m.cost > 0 || m.unpriced) && (
                <ChartCard title="Cost by model">
                  <CostByModelChart models={models} />
                </ChartCard>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
